import { EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { TableColumnsType } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import {
  createCurrency,
  listCurrencies,
  setCurrencyActive,
  updateCurrency,
} from '../api/currencies';
import type { Currency } from '../api/currencies';
import { ApiError } from '../api/errors';
import { PermissionCodes } from '../auth/permissions';
import { useAuth } from '../auth/useAuth';

/** Giá trị của biểu mẫu thêm hoặc sửa tiền tệ. */
interface CurrencyFormValues {
  readonly code: string;
  readonly name: string;
  readonly nameEn: string;
  readonly symbol: string | null;
  readonly decimalPlaces: number;
  readonly roundingUnit: number | null;
  readonly sortOrder: number;
}

/** Dữ liệu của một trang đã nạp xong, kèm khóa nhận dạng lần nạp đó. */
interface LoadedPage {
  readonly key: string;
  readonly items: readonly Currency[];
  readonly totalCount: number;
}

/** Số bản ghi mỗi trang. Khớp với mặc định của máy chủ. */
const pageSize = 25;

/**
 * Trang danh mục tiền tệ.
 *
 * Ba điểm khác với danh mục đơn vị đo, và đều bắt nguồn từ nghiệp vụ:
 *
 * 1. **Không có nút xóa.** Mã tiền tệ là mã quốc tế, và một đồng tiền đã xuất hiện trên hóa đơn thì
 *    phải còn để tra lại. Thứ cần là ngừng cho chọn nó.
 * 2. **Không ngừng dùng được đồng tiền hạch toán của pháp nhân.** Máy chủ trả về sẵn dấu hiệu đó nên
 *    nút bị vô hiệu hóa kèm lời giải thích, thay vì để người dùng bấm rồi nhận thông báo từ chối.
 * 3. **Số chữ số thập phân chỉ dùng để hiển thị và làm tròn tổng chứng từ.** Trong cơ sở dữ liệu mọi
 *    số tiền vẫn giữ bốn chữ số thập phân, vì đơn giá và kết quả nhân tỷ giá cần độ chính xác đó.
 */
export function CurrencyPage(): ReactNode {
  const { hasPermission } = useAuth();

  const canManage = hasPermission(PermissionCodes.CurrencyManage);

  const [form] = Form.useForm<CurrencyFormValues>();

  const [pageNumber, setPageNumber] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const [loaded, setLoaded] = useState<LoadedPage | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [editingCurrency, setEditingCurrency] = useState<Currency | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const requestKey = useMemo(
    () => JSON.stringify({ searchTerm, includeInactive, pageNumber, reloadToken }),
    [searchTerm, includeInactive, pageNumber, reloadToken],
  );

  // Trạng thái đang tải được suy ra chứ không giữ thành biến trạng thái riêng — xem giải thích đầy đủ
  // ở trang danh mục đơn vị đo.
  const isLoading = loaded?.key !== requestKey && errorMessage === null;

  const reload = useCallback((): void => {
    setReloadToken((token) => token + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    void (async () => {
      try {
        const page = await listCurrencies({
          searchTerm,
          includeInactive,
          pageNumber,
          pageSize,
        });

        if (controller.signal.aborted) {
          return;
        }

        setErrorMessage(null);
        setLoaded({ key: requestKey, items: page.items, totalCount: page.totalCount });
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }

        setErrorMessage(
          error instanceof ApiError
            ? error.message
            : 'Không kết nối được tới máy chủ. Kiểm tra lại đường mạng.',
        );
      }
    })();

    return () => {
      controller.abort();
    };
  }, [requestKey, searchTerm, includeInactive, pageNumber]);

  const items = useMemo<readonly Currency[]>(() => loaded?.items ?? [], [loaded]);
  const totalCount = loaded?.totalCount ?? 0;

  function openCreateForm(): void {
    setEditingCurrency(null);
    form.setFieldsValue({
      code: '',
      name: '',
      nameEn: '',
      symbol: null,
      decimalPlaces: 2,
      roundingUnit: null,
      sortOrder: 900,
    });
    setIsFormOpen(true);
  }

  function openEditForm(currency: Currency): void {
    setEditingCurrency(currency);
    form.setFieldsValue({
      code: currency.code,
      name: currency.name,
      nameEn: currency.nameEn,
      symbol: currency.symbol,
      decimalPlaces: currency.decimalPlaces,
      roundingUnit: currency.roundingUnit,
      sortOrder: currency.sortOrder,
    });
    setIsFormOpen(true);
  }

  async function handleSubmit(values: CurrencyFormValues): Promise<void> {
    setIsSaving(true);

    try {
      if (editingCurrency === null) {
        await createCurrency({
          code: values.code,
          name: values.name,
          nameEn: values.nameEn,
          symbol: values.symbol,
          decimalPlaces: values.decimalPlaces,
          roundingUnit: values.roundingUnit,
          sortOrder: values.sortOrder,
        });

        void message.success('Đã thêm tiền tệ.');
      } else {
        await updateCurrency(editingCurrency.id, {
          name: values.name,
          nameEn: values.nameEn,
          symbol: values.symbol,
          decimalPlaces: values.decimalPlaces,
          roundingUnit: values.roundingUnit,
          sortOrder: values.sortOrder,
        });

        void message.success('Đã lưu thay đổi.');
      }

      setIsFormOpen(false);
      reload();
    } catch (error) {
      void message.error(
        error instanceof ApiError ? error.message : 'Không lưu được. Thử lại sau.',
      );
    } finally {
      setIsSaving(false);
    }
  }

  async function handleToggleActive(currency: Currency): Promise<void> {
    try {
      await setCurrencyActive(currency.id, !currency.isActive);
      reload();
    } catch (error) {
      void message.error(
        error instanceof ApiError ? error.message : 'Không đổi được trạng thái.',
      );
    }
  }

  const columns: TableColumnsType<Currency> = [
    {
      title: 'Mã',
      dataIndex: 'code',
      width: 90,
      render: (code: string) => <Typography.Text strong>{code}</Typography.Text>,
    },
    {
      title: 'Ký hiệu',
      dataIndex: 'symbol',
      width: 90,
      render: (symbol: string | null) => symbol ?? '—',
    },
    { title: 'Tên', dataIndex: 'name' },
    { title: 'Tên tiếng Anh', dataIndex: 'nameEn' },
    {
      title: 'Số lẻ',
      dataIndex: 'decimalPlaces',
      width: 80,
      align: 'right',
    },
    {
      title: 'Làm tròn',
      dataIndex: 'roundingUnit',
      width: 110,
      align: 'right',
      render: (roundingUnit: number | null) =>
        roundingUnit === null ? '—' : roundingUnit.toLocaleString('vi-VN'),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'isActive',
      width: 190,
      render: (_isActive: boolean, currency: Currency) => (
        <Space size={4}>
          {currency.isActive ? <Tag color="green">Đang dùng</Tag> : <Tag>Đã ngừng</Tag>}
          {currency.isCompanyBaseCurrency && <Tag color="gold">Đồng hạch toán</Tag>}
        </Space>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 150,
      render: (_value: unknown, currency: Currency) =>
        canManage ? (
          <Space size={4}>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                openEditForm(currency);
              }}
            >
              Sửa
            </Button>

            {currency.isActive && currency.isCompanyBaseCurrency ? (
              <Tooltip title="Đây là đồng tiền hạch toán của một pháp nhân nên không ngừng dùng được.">
                <Button size="small" disabled>
                  Ngừng
                </Button>
              </Tooltip>
            ) : (
              <Button
                size="small"
                onClick={() => {
                  void handleToggleActive(currency);
                }}
              >
                {currency.isActive ? 'Ngừng' : 'Dùng lại'}
              </Button>
            )}
          </Space>
        ) : null,
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Typography.Title level={4} style={{ margin: 0 }}>
        Tiền tệ
      </Typography.Title>

      {errorMessage !== null && <Alert type="error" showIcon message={errorMessage} />}

      <Space wrap>
        <Input.Search
          placeholder="Tìm theo mã hoặc tên"
          allowClear
          style={{ width: 260 }}
          onSearch={(value) => {
            setPageNumber(1);
            setSearchTerm(value);
          }}
        />

        <Space size={4}>
          <Switch
            checked={includeInactive}
            onChange={(checked) => {
              setPageNumber(1);
              setIncludeInactive(checked);
            }}
          />
          <Typography.Text>Hiện cả tiền tệ đã ngừng</Typography.Text>
        </Space>

        <Button icon={<ReloadOutlined />} onClick={reload}>
          Tải lại
        </Button>

        {canManage && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateForm}>
            Thêm tiền tệ
          </Button>
        )}
      </Space>

      <Table<Currency>
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={[...items]}
        loading={isLoading}
        scroll={{ x: 'max-content' }}
        pagination={{
          current: pageNumber,
          pageSize,
          total: totalCount,
          showSizeChanger: false,
          showTotal: (total, range) =>
            `${range[0].toLocaleString('vi-VN')}–${range[1].toLocaleString('vi-VN')}`
            + ` trong ${total.toLocaleString('vi-VN')}`,
          onChange: setPageNumber,
        }}
      />

      <Modal
        title={editingCurrency === null ? 'Thêm tiền tệ' : `Sửa tiền tệ ${editingCurrency.code}`}
        open={isFormOpen}
        confirmLoading={isSaving}
        onOk={() => {
          form.submit();
        }}
        onCancel={() => {
          setIsFormOpen(false);
        }}
        okText="Lưu"
        cancelText="Hủy"
        destroyOnHidden
      >
        <Form<CurrencyFormValues>
          form={form}
          layout="vertical"
          requiredMark={false}
          disabled={isSaving}
          onFinish={(values) => {
            void handleSubmit(values);
          }}
        >
          <Form.Item
            name="code"
            label="Mã tiền tệ"
            extra="Đúng ba chữ in hoa theo chuẩn ISO 4217. Ví dụ: THB, MYR, AUD."
            rules={[{ required: true, message: 'Chưa nhập mã tiền tệ.' }]}
          >
            {/* Không sửa được mã: nó xuất hiện trên hóa đơn đã phát hành và tờ khai thuế đã gửi. */}
            <Input disabled={editingCurrency !== null} />
          </Form.Item>

          <Form.Item
            name="name"
            label="Tên tiếng Việt"
            rules={[{ required: true, message: 'Chưa nhập tên tiếng Việt.' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="nameEn"
            label="Tên tiếng Anh"
            extra="In trên hóa đơn xuất khẩu và lệnh chuyển tiền quốc tế."
            rules={[{ required: true, message: 'Chưa nhập tên tiếng Anh.' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="symbol" label="Ký hiệu" extra="Để trống nếu không có ký hiệu riêng.">
            <Input placeholder="₫, $, €" />
          </Form.Item>

          <Form.Item
            name="decimalPlaces"
            label="Số chữ số thập phân"
            extra="Chỉ dùng để hiển thị và làm tròn tổng chứng từ. Đồng Việt Nam và yên Nhật là 0."
            rules={[{ required: true, message: 'Chưa nhập số chữ số thập phân.' }]}
          >
            <InputNumber min={0} max={4} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="roundingUnit"
            label="Đơn vị làm tròn tổng tiền"
            extra="Để trống nếu không làm tròn thêm. Đồng Việt Nam thường là 1; dùng 100 nếu không muốn số lẻ hàng chục."
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="sortOrder"
            label="Thứ tự trình bày"
            extra="Số nhỏ nằm trên. Đồng tiền hay dùng nên để số nhỏ."
            rules={[{ required: true, message: 'Chưa nhập thứ tự trình bày.' }]}
          >
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
